import {
  forwardRef,
  type InputHTMLAttributes,
  type ReactNode,
  type TextareaHTMLAttributes,
} from 'react';
import { cx } from '../cx';
import { Field, fieldStyles as styles, type FieldProps } from '../Field/Field';

// Input / Textarea — DESIGN_SYSTEM §12 row 2 (`.input`, `.textarea`) and row 3, the input group with a
// currency prefix and a period suffix (`.input-group`: "$ 25.00 per day"). Tokens only; states: default ·
// hover · focus · error · disabled. Money and metrics get tabular numerals (`numeric`).

type FieldFrameProps = Omit<FieldProps, 'children' | 'id' | 'className'>;

export interface InputProps
  extends FieldFrameProps, Omit<InputHTMLAttributes<HTMLInputElement>, 'size' | 'id' | 'prefix'> {
  id?: string;
  size?: 'sm' | 'md' | 'lg';
  /** Affix before the value, e.g. the currency symbol. */
  prefix?: ReactNode;
  /** Affix after the value, e.g. "per day" — money always shows currency AND period (row 3). */
  suffix?: ReactNode;
  /** Tabular numerals for money and metrics (§5). */
  numeric?: boolean;
  /** Class name for the frame around label + control. */
  className?: string;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  {
    label,
    optional,
    hint,
    error,
    testID,
    id,
    size = 'md',
    prefix,
    suffix,
    numeric = false,
    className,
    disabled,
    ...rest
  },
  ref,
) {
  const sizeClass = cx(size === 'sm' && styles.sm, size === 'lg' && styles.lg);
  const grouped = prefix !== undefined || suffix !== undefined;
  return (
    <Field
      label={label}
      optional={optional}
      hint={hint}
      error={error}
      id={id}
      className={className}
    >
      {(control) =>
        grouped ? (
          <span
            className={cx(
              styles.box,
              styles.group,
              sizeClass,
              error ? styles.invalid : undefined,
              disabled ? styles.disabled : undefined,
            )}
          >
            {prefix !== undefined ? <span className={styles.affix}>{prefix}</span> : null}
            <input
              ref={ref}
              className={cx(numeric && styles.num)}
              disabled={disabled}
              data-testid={testID}
              {...control}
              {...rest}
            />
            {suffix !== undefined ? <span className={styles.affix}>{suffix}</span> : null}
          </span>
        ) : (
          <input
            ref={ref}
            className={cx(
              styles.box,
              sizeClass,
              error ? styles.invalid : undefined,
              numeric && styles.num,
            )}
            disabled={disabled}
            data-testid={testID}
            {...control}
            {...rest}
          />
        )
      }
    </Field>
  );
});

export interface TextareaProps
  extends FieldFrameProps, Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'id'> {
  id?: string;
  className?: string;
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea(
  { label, optional, hint, error, testID, id, className, ...rest },
  ref,
) {
  return (
    <Field
      label={label}
      optional={optional}
      hint={hint}
      error={error}
      id={id}
      className={className}
    >
      {(control) => (
        <textarea
          ref={ref}
          className={cx(styles.box, styles.textarea, error ? styles.invalid : undefined)}
          data-testid={testID}
          {...control}
          {...rest}
        />
      )}
    </Field>
  );
});
